"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { CAST, CAST_BY_ID, DEFAULT_CAST_ID, DEMO_ASOF } from "@/modules/lernpfad/data/cast"
import { DEMO_CATALOG } from "@/modules/lernpfad/data/obligations"
import {
    addMonths,
    composeCurriculum,
    computeDueObligations,
    computeGaps,
} from "@/modules/lernpfad/engine"
import { countOverrides, resolvePolicy } from "@/modules/lernpfad/engine"
import type {
    ComposerPolicy,
    Constraints,
    EmployeeProfile,
    LearningEvidence,
    RequiredDriver,
    SkillSource,
} from "@/modules/lernpfad/engine"
import { FRAMEWORK_BY_ID, frameworkPatch } from "@/modules/lernpfad/data/frameworks"
import type { FrameworkId } from "@/modules/lernpfad/data/frameworks"
import { createLocalizer } from "@/modules/lernpfad/i18n/localize"
import { rationaleStringsFor } from "@/modules/lernpfad/i18n/rationale-strings"
import type { Lang } from "@/modules/lernpfad/i18n/ui"
import type { ConnectorId } from "@/modules/lernpfad/data/connectors"

export type Persona = "mitarbeitende" | "fuehrung" | "hr" | "integrationen" | "steuerung"

export type DemoStage = "landing" | "app"

export type ConnectionStatus = "idle" | "connecting" | "connected"

export interface ConnectionState {
    status: ConnectionStatus
    syncedCourses?: number
    /** True once the connect wizard's last-sync line should read "just now". */
    freshlySynced?: boolean
}

/** A completion that "arrived from the LMS" — same engine input as markDone. */
export interface LmsCompletion {
    bausteinId: string
    via: "lti" | "sync"
}

export const ALL_SOURCES: SkillSource[] = [
    "stellenprofil",
    "lernhistorie",
    "cv",
    "mag",
    "selbsteinschaetzung",
]

/** Stable identity so an untouched override object never re-triggers a memo. */
const EMPTY_POLICY: ComposerPolicy = {}

/** Immutable set-by-path. Creates missing levels; never mutates the input. */
function setByPath<T extends object>(source: T, path: string[], value: unknown): T {
    const [head, ...rest] = path
    const current = (source as Record<string, unknown>)[head]
    return {
        ...source,
        [head]:
            rest.length === 0
                ? value
                : setByPath(
                      (current !== null && typeof current === "object" ? current : {}) as object,
                      rest,
                      value,
                  ),
    }
}

/** Merge a manual override object onto a framework preset. */
function mergePolicy(base: ComposerPolicy, patch: ComposerPolicy): ComposerPolicy {
    const result: Record<string, unknown> = { ...(base as Record<string, unknown>) }
    for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
        if (value === undefined) continue
        const existing = result[key]
        result[key] =
            value !== null &&
            typeof value === "object" &&
            existing !== null &&
            typeof existing === "object"
                ? mergePolicy(existing as ComposerPolicy, value as ComposerPolicy)
                : value
    }
    return result as ComposerPolicy
}

/**
 * Trailing debounce.
 *
 * Deliberately NOT `useDeferredValue`: that yields to render priority, which
 * still fires one request per settled frame. What is needed here is a hard
 * ceiling on how often the 1,960-person aggregation is asked for.
 */
function useDebounced<T>(value: T, delayMs: number): T {
    const [settled, setSettled] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setSettled(value), delayMs)
        return () => clearTimeout(timer)
    }, [value, delayMs])
    return settled
}

/**
 * Demo state — every lever the stage needs, and nothing else.
 *
 * The levers deliberately mutate the ENGINE INPUT rather than the rendered
 * output: changing Pensum re-runs `composeCurriculum`, it does not filter a
 * precomputed list. That is what makes "what if she worked 60%?" answerable
 * live instead of being a rehearsed slide (plan §1).
 */
export function useLernpfadDemo() {
    const [stage, setStage] = useState<DemoStage>("landing")
    const [persona, setPersona] = useState<Persona>("mitarbeitende")
    const [lang, setLang] = useState<Lang>("de")

    /**
     * Guided path. `null` = not running; otherwise the index into
     * `TOUR_STEPS`. Held here rather than in the tour component because a step
     * drives the same levers a viewer can drive by hand — switching persona,
     * moving Pensum, applying a framework — and those all live in this hook.
     */
    const [tourStep, setTourStep] = useState<number | null>(null)

    // Simulated integration state. No network, all resettable — but it feeds
    // the SAME engine inputs the seed feeds, so every "integration event"
    // ends in a genuine recomputation rather than a canned screen.
    const [connections, setConnections] = useState<Partial<Record<ConnectorId, ConnectionState>>>({})
    const [lmsCompletions, setLmsCompletions] = useState<LmsCompletion[]>([])
    const [syncBoost, setSyncBoost] = useState(false)
    const [employeeId, setEmployeeId] = useState(DEFAULT_CAST_ID)

    /**
     * The simulated LMS overlay. `null` = closed. A non-null
     * `initialBausteinId` means "Hier starten" launched it — an LTI deep-link
     * lands a learner directly in the course, so the shell opens straight into
     * the player rather than the catalogue. `null` id = opened from the
     * Integrationen tile, at the catalogue root.
     */
    const [lmsShell, setLmsShell] = useState<{ initialBausteinId: string | null } | null>(null)

    /**
     * Evidence the LMS surface has produced, upserted by Baustein id.
     *
     * Deliberately NOT employee-scoped, matching `lmsCompletions`'s existing
     * convention (switching cast members keeps whatever was recorded for the
     * previous one) — introducing a different scoping rule for a closely
     * related piece of state would be a second, inconsistent quirk rather than
     * a fix for the first.
     */
    const [lmsEvidence, setLmsEvidence] = useState<LearningEvidence[]>([])

    // Levers
    const [pensumOverride, setPensumOverride] = useState<number | null>(null)
    const [hoursPerQuarter, setHoursPerQuarter] = useState(16)
    const [aspirationOverride, setAspirationOverride] = useState<string | null>(null)
    const [monthOffset, setMonthOffset] = useState(0)
    const [horizonMonths, setHorizonMonths] = useState(12)
    const [allowedSources, setAllowedSources] = useState<SkillSource[]>(ALL_SOURCES)
    const [extraCompleted, setExtraCompleted] = useState<string[]>([])

    // Composer policy — the Steuerung view's output. Two layers, because the
    // distinction is what the UI has to show: a framework applies a preset, and
    // anything edited afterwards is an override that marks the card "angepasst".
    const [frameworks, setFrameworks] = useState<FrameworkId[]>([])
    const [overrides, setOverrides] = useState<ComposerPolicy>(EMPTY_POLICY)

    /**
     * Transfer confirmations made by a manager, keyed `employeeId:bausteinId`.
     *
     * The value is the set of required drivers they put in place — an empty
     * array means "confirmed as NOT applied", which is a different statement
     * from "not yet answered" and has to stay distinguishable.
     */
    const [confirmedTransfers, setConfirmedTransfers] = useState<
        Record<string, RequiredDriver[]>
    >({})

    const baseEmployee = CAST_BY_ID.get(employeeId) ?? CAST[0]

    // One localizer per language change, shared by every view and every chart.
    const localizer = useMemo(() => createLocalizer(lang), [lang])
    const rationaleStrings = useMemo(() => rationaleStringsFor(localizer), [localizer])

    const asOf = useMemo(() => addMonths(DEMO_ASOF, monthOffset), [monthOffset])

    /**
     * The effective policy: framework presets first, manual edits on top.
     *
     * `undefined` when nothing is set, and that is not laziness — `resolvePolicy`
     * returns `DEFAULT_POLICY` by identity for `undefined`, which keeps its
     * WeakMap fast path free across the ~6,000 resolutions a population summary
     * performs, and keeps react-query from holding two cache entries that
     * compute the same answer.
     */
    const policy: ComposerPolicy | undefined = useMemo(() => {
        const merged = mergePolicy(frameworkPatch(frameworks), overrides)
        return Object.keys(merged).length > 0 ? merged : undefined
    }, [frameworks, overrides])

    /**
     * The policy the POPULATION query is keyed on, trailing the live one.
     *
     * Aggregating 1,960 people costs ~25 ms, and a slider dragged at 60 Hz would
     * ask for that sixty times a second on the same process that serves the app.
     * The controls read `policy` and stay instant; only the expensive query
     * waits.
     */
    const policyCommitted = useDebounced(policy, 250)

    const constraints: Constraints = useMemo(
        () => ({
            asOf,
            horizonMonths,
            hoursPerQuarterAtFullTime: hoursPerQuarter,
            allowedSources,
            ...(policy ? { policy } : {}),
        }),
        [asOf, horizonMonths, hoursPerQuarter, allowedSources, policy],
    )

    /** Same constraints, but with the debounced policy — for the cockpit query. */
    const committedConstraints: Constraints = useMemo(
        () => ({
            asOf,
            horizonMonths,
            hoursPerQuarterAtFullTime: hoursPerQuarter,
            allowedSources,
            ...(policyCommitted ? { policy: policyCommitted } : {}),
        }),
        [asOf, horizonMonths, hoursPerQuarter, allowedSources, policyCommitted],
    )

    /** The same constraints with NO policy — the baseline every impact compares against. */
    const baselineConstraints: Constraints = useMemo(
        () => ({ asOf, horizonMonths, hoursPerQuarterAtFullTime: hoursPerQuarter, allowedSources }),
        [asOf, horizonMonths, hoursPerQuarter, allowedSources],
    )

    /**
     * Apply a manager's confirmations to a profile.
     *
     * This is the closed loop, and it is the reason the manager view is not just
     * a form: a confirmation becomes Level 3 evidence on the profile, and under
     * an evidence-gated policy that immediately changes what the engine believes
     * the person can do — so their plan recomputes on the spot.
     */
    const withConfirmations = useCallback(
        (profile: EmployeeProfile): EmployeeProfile => {
            const keys = Object.keys(confirmedTransfers).filter((key) =>
                key.startsWith(`${profile.id}:`),
            )
            if (keys.length === 0) return profile

            const evidence = [...(profile.evidence ?? [])]
            for (const key of keys) {
                const bausteinId = key.slice(profile.id.length + 1)
                const drivers = confirmedTransfers[key]
                const behaviour = {
                    applied: (drivers.length > 0 ? "yes" : "no") as "yes" | "no",
                    selfReported: false,
                    verifiedBy: profile.managerId ?? "manager",
                    on: asOf,
                    drivers,
                }
                const index = evidence.findIndex((entry) => entry.bausteinId === bausteinId)
                if (index >= 0) evidence[index] = { ...evidence[index], behaviour }
                else evidence.push({ bausteinId, behaviour })
            }
            return { ...profile, evidence }
        },
        [confirmedTransfers, asOf],
    )

    /**
     * Merge evidence the LMS surface produced on top of a profile's own.
     *
     * The same "apply a post-hoc patch to whoever is being viewed" shape as
     * `withConfirmations`, kept as a SEPARATE function rather than folded in:
     * an LMS record carries `reaction` and `learning` together, a manager
     * confirmation only ever carries `behaviour`, and merging both onto the
     * SAME `bausteinId` entry (rather than one overwriting the other) is the
     * point — a course can accumulate an L1/L2 result from the learner and an
     * L3 confirmation from their manager without either erasing the other.
     *
     * Deliberately applied to `employee` only, never to `team`: `lmsEvidence`
     * carries no employee id (matching `lmsCompletions`'s existing shape), so
     * applying it broadly would leak whatever the CURRENTLY VIEWED person just
     * did onto every report who happens to share that same Baustein id.
     */
    const withLmsEvidence = useCallback(
        (profile: EmployeeProfile): EmployeeProfile => {
            if (lmsEvidence.length === 0) return profile
            const evidence = [...(profile.evidence ?? [])]
            for (const patch of lmsEvidence) {
                const index = evidence.findIndex((entry) => entry.bausteinId === patch.bausteinId)
                if (index >= 0) evidence[index] = { ...evidence[index], ...patch }
                else evidence.push(patch)
            }
            return { ...profile, evidence }
        },
        [lmsEvidence],
    )

    const confirmTransfer = useCallback(
        (employeeId: string, bausteinId: string, drivers: RequiredDriver[]) => {
            setConfirmedTransfers((current) => ({
                ...current,
                [`${employeeId}:${bausteinId}`]: drivers,
            }))
        },
        [],
    )

    /**
     * The employee as the levers currently describe them, with any manager
     * confirmation or LMS-recorded evidence layered on top.
     */
    const employee: EmployeeProfile = useMemo(
        () =>
            withConfirmations(
                withLmsEvidence({
                    ...baseEmployee,
                    pensum: pensumOverride ?? baseEmployee.pensum,
                    aspiration: aspirationOverride ?? baseEmployee.aspiration,
                    completed: [
                        ...baseEmployee.completed,
                        ...extraCompleted.map((bausteinId) => ({ bausteinId, completedOn: asOf })),
                        ...lmsCompletions.map(({ bausteinId }) => ({ bausteinId, completedOn: asOf })),
                    ],
                }),
            ),
        [
            baseEmployee,
            pensumOverride,
            aspirationOverride,
            extraCompleted,
            lmsCompletions,
            asOf,
            withConfirmations,
            withLmsEvidence,
        ],
    )

    const curriculum = useMemo(
        () => composeCurriculum(employee, DEMO_CATALOG, constraints),
        [employee, constraints],
    )
    const gaps = useMemo(() => computeGaps(employee, DEMO_CATALOG, constraints), [employee, constraints])
    const dueObligations = useMemo(
        () => computeDueObligations(employee, DEMO_CATALOG, constraints),
        [employee, constraints],
    )

    /** Andrea's team, for the manager view. */
    const team = useMemo(
        () => CAST.filter((member) => member.managerId === employeeId).map(withConfirmations),
        [employeeId, withConfirmations],
    )

    const markDone = useCallback((bausteinId: string) => {
        setExtraCompleted((current) =>
            current.includes(bausteinId) ? current : [...current, bausteinId],
        )
    }, [])

    /** A completion delivered by the (simulated) LMS — LTI passback or sync. */
    const recordLmsCompletion = useCallback((bausteinId: string, via: LmsCompletion["via"]) => {
        setLmsCompletions((current) =>
            current.some((entry) => entry.bausteinId === bausteinId)
                ? current
                : [...current, { bausteinId, via }],
        )
    }, [])

    const openLms = useCallback((bausteinId?: string) => {
        setLmsShell({ initialBausteinId: bausteinId ?? null })
    }, [])

    const closeLms = useCallback(() => setLmsShell(null), [])

    /**
     * Evidence finishing a course in the LMS surface actually produced —
     * reaction, a scored result, or both. Also records the plain completion,
     * exactly as `recordLmsCompletion` already does for the round-trip flow:
     * finishing a course is always both things at once.
     */
    const recordLmsEvidence = useCallback((evidence: LearningEvidence) => {
        setLmsEvidence((current) => {
            const index = current.findIndex((entry) => entry.bausteinId === evidence.bausteinId)
            if (index === -1) return [...current, evidence]
            const next = [...current]
            next[index] = { ...next[index], ...evidence }
            return next
        })
        recordLmsCompletion(evidence.bausteinId, "lti")
    }, [recordLmsCompletion])

    const setConnection = useCallback((id: ConnectorId, state: ConnectionState | null) => {
        setConnections((current) => {
            const next = { ...current }
            if (state === null) delete next[id]
            else next[id] = state
            return next
        })
        // Disconnecting the record also withdraws everything it delivered —
        // the ripple must be reversible or re-running the demo needs a reload.
        if (state === null) {
            setLmsCompletions([])
            setSyncBoost(false)
        }
    }, [])

    const moodleConnected = connections.moodle?.status === "connected"

    /**
     * Turn a framework on or off.
     *
     * Selecting a ladder replaces any other ladder rather than stacking: the
     * three are competing answers to the same question, and a UI that let both
     * be "on" would be describing something the literature does not support.
     */
    const toggleFramework = useCallback((id: FrameworkId) => {
        setFrameworks((current) => {
            if (current.includes(id)) return current.filter((entry) => entry !== id)
            const family = FRAMEWORK_BY_ID.get(id)?.family
            const kept =
                family === "ladder"
                    ? current.filter((entry) => FRAMEWORK_BY_ID.get(entry)?.family !== "ladder")
                    : current
            return [...kept, id]
        })
    }, [])

    /**
     * The ladder swap that just happened, surfaced for ~2.5 s.
     *
     * Picking a ladder silently evicts the one already held. That is correct,
     * but it used to happen with no feedback at all: a row the viewer was not
     * looking at simply turned off. (The panel carried a "conflict" banner for
     * exactly this, but it was unreachable — `toggleFramework` evicts before
     * anything could ever observe two ladders coexisting.)
     *
     * Derived by diffing commits rather than recorded inside the updater: a
     * state updater must stay pure, since React may run it twice. A swap is
     * precisely one id added and one removed in the same commit, which is
     * exactly what this compares — and it keeps `toggleFramework`'s identity
     * stable, which the guided tour depends on.
     */
    const [lastSwap, setLastSwap] = useState<{ from: FrameworkId; to: FrameworkId } | null>(null)
    const previousFrameworks = useRef<FrameworkId[]>([])

    useEffect(() => {
        const previous = previousFrameworks.current
        previousFrameworks.current = frameworks
        const added = frameworks.find((entry) => !previous.includes(entry))
        const removed = previous.find((entry) => !frameworks.includes(entry))
        if (!added || !removed) return
        setLastSwap({ from: removed, to: added })
        const timer = window.setTimeout(() => setLastSwap(null), 2500)
        return () => window.clearTimeout(timer)
    }, [frameworks])

    /**
     * Jump from a framework's cluster pill to that cluster's controls. Set by
     * `SteuerungView`, which owns the tab state; a no-op elsewhere.
     */
    const openClusterRef = useRef<(cluster: string) => void>(() => {})
    const openCluster = useCallback((cluster: string) => openClusterRef.current(cluster), [])
    const setOpenCluster = useCallback((fn: (cluster: string) => void) => {
        openClusterRef.current = fn
    }, [])

    /** Set one parameter by dotted path, e.g. `ranking.denominator`. */
    const setParam = useCallback((path: string, value: unknown) => {
        setOverrides((current) => setByPath(current, path.split("."), value))
    }, [])

    const resetPolicy = useCallback(() => {
        setFrameworks([])
        setOverrides(EMPTY_POLICY)
        setConfirmedTransfers({})
    }, [])

    /** How far the live policy has drifted from the bank's standard settings. */
    const overrideCount = useMemo(
        () => (policy ? countOverrides(resolvePolicy(policy)) : 0),
        [policy],
    )

    const toggleSource = useCallback((source: SkillSource) => {
        setAllowedSources((current) =>
            current.includes(source)
                ? current.filter((entry) => entry !== source)
                : [...current, source],
        )
    }, [])

    /**
     * Guided path. `startTour` also leaves the landing stage, so the single
     * landing CTA both enters the demo and begins the story.
     */
    const startTour = useCallback(() => {
        setStage("app")
        setTourStep(0)
    }, [])

    const nextTourStep = useCallback(() => {
        setTourStep((current) => (current === null ? null : current + 1))
    }, [])

    const endTour = useCallback(() => setTourStep(null), [])

    const reset = useCallback(() => {
        setConnections({})
        setLmsCompletions([])
        setLmsEvidence([])
        setLmsShell(null)
        setSyncBoost(false)
        setPensumOverride(null)
        setHoursPerQuarter(16)
        setAspirationOverride(null)
        setMonthOffset(0)
        setHorizonMonths(12)
        setAllowedSources(ALL_SOURCES)
        setExtraCompleted([])
        setFrameworks([])
        setOverrides(EMPTY_POLICY)
        setConfirmedTransfers({})
        setTourStep(null)
    }, [])

    const selectEmployee = useCallback((id: string) => {
        setEmployeeId(id)
        // Levers are per-person: carrying Sandra's 40% override onto Thomas
        // would silently misrepresent him on stage.
        setPensumOverride(null)
        setAspirationOverride(null)
        setExtraCompleted([])
    }, [])

    const leversTouched =
        pensumOverride !== null ||
        aspirationOverride !== null ||
        hoursPerQuarter !== 16 ||
        monthOffset !== 0 ||
        horizonMonths !== 12 ||
        allowedSources.length !== ALL_SOURCES.length ||
        extraCompleted.length > 0 ||
        lmsCompletions.length > 0 ||
        policy !== undefined

    return {
        stage,
        setStage,
        persona,
        setPersona,
        // guided path
        tourStep,
        startTour,
        nextTourStep,
        endTour,
        connections,
        setConnection,
        moodleConnected,
        lmsCompletions,
        recordLmsCompletion,
        lmsShell,
        openLms,
        closeLms,
        lmsEvidence,
        recordLmsEvidence,
        syncBoost,
        setSyncBoost,
        lang,
        setLang,
        localizer,
        t: localizer.t,
        rationaleStrings,
        employeeId,
        selectEmployee,
        employee,
        baseEmployee,
        team,
        curriculum,
        gaps,
        dueObligations,
        constraints,
        committedConstraints,
        baselineConstraints,
        asOf,
        // composer policy (Steuerung)
        frameworks,
        toggleFramework,
        lastSwap,
        openCluster,
        setOpenCluster,
        confirmedTransfers,
        confirmTransfer,
        withConfirmations,
        policy,
        overrides,
        setParam,
        resetPolicy,
        overrideCount,
        // levers
        pensumOverride,
        setPensumOverride,
        hoursPerQuarter,
        setHoursPerQuarter,
        aspirationOverride,
        setAspirationOverride,
        monthOffset,
        setMonthOffset,
        horizonMonths,
        setHorizonMonths,
        allowedSources,
        toggleSource,
        extraCompleted,
        markDone,
        leversTouched,
        reset,
    }
}

export type LernpfadDemoState = ReturnType<typeof useLernpfadDemo>
